import {
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SmsIcon from '@mui/icons-material/Sms';
import { useTranslation } from 'react-i18next';
import { Browser } from '@capacitor/browser';
import { Link, LinkType } from 'shared/types';
import { formatPhoneNumber } from 'shared/utils';
import { getAbsoluteUrl } from 'shared/getAbsoluteUrl';
import { Capacitor } from '@capacitor/core';
import { LinkTypes, PhoneSMSRegex } from '../../../../constants';

type GeoEventLinkItemProps = {
  link: Link;
};

const mapLinkTypeToLabelTransKey: Record<LinkType, string> = {
  [LinkTypes.phone]: 'links.phone',
  [LinkTypes.website]: 'links.website',
  [LinkTypes.email]: 'links.email',
};

const mapLinkTypeToIcon: Record<LinkType, (link: Link) => JSX.Element> = {
  [LinkTypes.phone]: ({ linkType, value }) =>
    linkType === LinkTypes.phone && PhoneSMSRegex.test(value) ? (
      <SmsIcon />
    ) : (
      <PhoneIcon />
    ),
  [LinkTypes.website]: () => <OpenInNewIcon />,
  [LinkTypes.email]: () => <EmailIcon />,
};

export const GeoEventLinkItem = (props: GeoEventLinkItemProps): JSX.Element => {
  const { link } = props;
  const { t } = useTranslation();

  const handleLinkClick = async (): Promise<void> => {
    if (link.linkType === LinkTypes.phone) {
      if (PhoneSMSRegex.test(link.value)) {
        const urlString =
          Capacitor.getPlatform() === 'ios'
            ? `sms:${link.value}&body=${link.label}`
            : `sms:${link.value}?body=${link.label}`;

        window.open(urlString);
        return;
      }

      window.open(`tel:${link.value}`);
      return;
    }

    if (link.linkType === LinkTypes.email) {
      window.open(`mailto:${link.value}`);
      return;
    }

    if (link.linkType === LinkTypes.website) {
      const url = getAbsoluteUrl(link.value);
      await Browser.open({ url, presentationStyle: 'popover' });
    }
  };

  return (
    <ListItemButton
      key={`${link.value}-${link.linkType}`}
      divider
      onClick={handleLinkClick}
    >
      <ListItemText
        primary={
          <Typography variant="h4" color="secondary">
            {t(mapLinkTypeToLabelTransKey[link.linkType as LinkType])}
          </Typography>
        }
        secondary={
          <>
            <Typography color="textPrimary" sx={{ fontWeight: 600 }}>
              {link.label}
            </Typography>
            {link.linkType !== LinkTypes.website && (
              <Typography color="textPrimary">
                {link.linkType === LinkTypes.phone
                  ? formatPhoneNumber(link.value)
                  : link.value}
              </Typography>
            )}
          </>
        }
        slotProps={{ secondary: { component: 'div' } }}
      />
      <ListItemIcon
        sx={(theme) => ({
          minWidth: 'fit-content',
          color: theme.palette.text.primary,
        })}
      >
        {mapLinkTypeToIcon[link.linkType as LinkType](link)}
      </ListItemIcon>
    </ListItemButton>
  );
};
